import { useEffect, useState } from 'react';
import { CrmOption } from '../../services/api/crmApi';
import { CrmFilters, CrmSourceKey, getCrmSource } from './crmSources';

const CACHE_MS = 60_000;
const cache = new Map<string, { at: number; promise: Promise<CrmOption[]> }>();

const cleanFilters = (filters?: CrmFilters): CrmFilters =>
  Object.fromEntries(
    Object.entries(filters || {}).filter(([, value]) => value?.trim()),
  );

/**
 * Loads a CRM option list. Identical requests share one in-flight/cached
 * promise for a minute, so several fields on a form don't each hit the CRM.
 * Searchable sources (leads) pass the visitor's `query`; each distinct query
 * is its own cached entry.
 */
export const useCrmOptions = (
  tenantId: string | undefined,
  source: CrmSourceKey | string | undefined,
  filters?: CrmFilters,
  enabled = true,
  query?: string,
) => {
  const [options, setOptions] = useState<CrmOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const def = getCrmSource(source);
  const active = cleanFilters(filters);
  const search = def.searchable ? (query || '').trim() : '';
  const cacheKey = `${tenantId}|${def.key}|${JSON.stringify(active)}|${search}`;

  useEffect(() => {
    if (!enabled || !tenantId) {
      setOptions([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);

    const hit = cache.get(cacheKey);
    const entry =
      hit && Date.now() - hit.at < CACHE_MS
        ? hit
        : {
            at: Date.now(),
            promise:
              search && def.search
                ? def.search(tenantId, search, active)
                : def.fetch(tenantId, active),
          };
    cache.set(cacheKey, entry);

    entry.promise
      .then((result) => {
        if (!cancelled) setOptions(result);
      })
      .catch(() => {
        cache.delete(cacheKey);
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // `active` is derived from `filters`; cacheKey already captures its content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, enabled]);

  return { options, loading, error, source: def };
};
